import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

const ROTATE_INTERVAL_MS = 5000;

const STATUS_MESSAGES = [
    'This may take a moment...',
    'Enhancing image details with AI...',
    'Almost there, refining quality...',
    'Sharpening edges and textures...',
    'Finalising high-resolution output...',
    'Optimising colour depth and clarity...',
    'Smoothing out artifacts and noise...',
    'Just a little longer, please wait...',
];

interface BoostLoadingOverlayProps {
    /** Optional className to override the default z-index or positioning */
    className?: string;
    /** 'upscale' for a fresh boost, 'comparison' when loading a previous comparison */
    variant?: 'upscale' | 'comparison';
}

/**
 * Animated loading overlay shown while the image upscale is in progress.
 * Renders a pulsing sparkle icon with rotating status text that changes
 * every few seconds to keep the user informed during the 12-30s process.
 */
export const BoostLoadingOverlay = ({
    className,
    variant = 'upscale',
}: BoostLoadingOverlayProps) => {
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setMessageIndex((prev) =>
                prev < STATUS_MESSAGES.length - 1 ? prev + 1 : prev,
            );
        }, ROTATE_INTERVAL_MS);

        return () => clearInterval(timer);
    }, []);

    return (
        <div
            className={cn(
                'absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/75',
                className,
            )}
        >
            <style>{`
                @keyframes boostSparkle {
                    0% { opacity: 1; transform: scale(1) rotate(0deg); }
                    25% { opacity: 0.7; transform: scale(1.15) rotate(0deg); }
                    50% { opacity: 1; transform: scale(1) rotate(0deg); }
                    75% { opacity: 0.7; transform: scale(1.15) rotate(0deg); }
                    85.7% { opacity: 1; transform: scale(1) rotate(0deg); }
                    100% { opacity: 1; transform: scale(1) rotate(360deg); }
                }
                .animate-boost-sparkle {
                    animation: boostSparkle 3.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
            `}</style>
            <div className="text-gray-800">
                <Sparkles className="animate-boost-sparkle h-8 w-8" />
            </div>
            <div className="mt-3 text-sm font-medium text-black">
                {variant === 'comparison'
                    ? 'Loading original image...'
                    : 'Boosting resolution...'}
            </div>
            {variant === 'upscale' && (
                <div className="mt-2 text-xs text-black/60 transition-opacity duration-500">
                    {STATUS_MESSAGES[messageIndex]}
                </div>
            )}
        </div>
    );
};
